import type { AbstractControl, FormGroup } from '@angular/forms';
import type { ReactiveFormRequestHandler } from '../../adapters/reactive-form-builder.adapters';
import type { Form, FormField } from '../../interfaces/form.interface';
import { FormType } from '../../interfaces/form.interface';
import { FormOperations } from '../form-operations';
import type {
  ReactiveFormPayloadSerializerResolvers,
} from '../../interfaces/reactive-form-serializer.interface';
import { ReactiveFormPayloadSerializer } from '../form-submission.utils';

export interface ReactiveFormSubmitFacadeConfiguration<T> {
    readonly getForm: () => Form<T> | null;
    readonly getModel: () => FormGroup | null;
    readonly getRecordHasAssociatedData: () => boolean;
    readonly getLockOnSuccess: () => boolean;
    readonly getRequest: () => ReactiveFormRequestHandler<any> | null;
    readonly getGetRequest: () => ReactiveFormRequestHandler<any> | null;
    readonly isDemoUser: () => boolean;
    readonly emitGoBack: () => void;
    readonly emitAfterSend: (response: T) => void;
    readonly getSerializerResolvers?: () => ReactiveFormPayloadSerializerResolvers;
    readonly scheduleRequestCleanup?: (cleanup: () => void) => void;
}

type SubmitButtonLabels = readonly [string, string, string];

type ValidationErrorFormatter = (errors: Record<string, string>) => string;

const REQUEST_CLEANUP_DELAY_MS = 1000;

/**
 * Owns submit-button state, payload serialization, validation summaries and request lifecycle side effects.
 */
export class ReactiveFormSubmitFacade<T> {
    public validationError: string | null = null;
    public validationSummary: string | null = null;
    public warning: string | null = null;

    private readonly requestErrorMessages: string[] = [];
    private hasSubmittedRequest = false;
    private hasCompletedRequest = false;
    private formOperations: FormOperations<T> | null = null;

    constructor(private readonly configuration: ReactiveFormSubmitFacadeConfiguration<T>) {}

    public get requestError(): readonly string[] {
        return this.requestErrorMessages;
    }

    public get buttonLabel(): string {
        const [defaultLabel, loadingLabel, doneLabel] = this.buttonLabels[this.currentFormType] ?? this.buttonLabels[FormType.CREATION];
        const request = this.configuration.getRequest();

        if (this.warning) {
            return this.warning;
        }

        if (this.hasRequestError) {
            return request?.hasErrorCliExists === true ? 'Cliente ya existe...' : this.requestErrorMessages[0] ?? '';
        }

        if (!this.hasSubmittedRequest) {
            return defaultLabel;
        }

        if (this.isRequestLoading) {
            return loadingLabel;
        }

        if (this.hasSuccessfulRequest) {
            return doneLabel;
        }

        return defaultLabel;
    }

    public get buttonClasses(): Record<string, boolean> {
        return {
            'btn-danger': Boolean(
                this.validationError ||
                (!this.warning && this.currentFormType === FormType.DELETION && !this.isRequestLoading && !this.hasSubmittedRequest)
            ),
            'btn-warning': Boolean(this.warning || this.hasRequestError || this.hasClientExistsError),
            'btn-primary': Boolean(
                !this.validationError &&
                !this.warning &&
                this.currentFormType !== FormType.DELETION &&
                !this.isRequestLoading &&
                !this.hasSubmittedRequest
            ),
            'btn-success': Boolean(!this.validationError && !this.warning && this.hasSuccessfulRequest),
            'btn-secondary': Boolean(this.hasSubmittedRequest && this.isRequestLoading)
        };
    }

    public get iconClasses(): Record<string, boolean> {
        return {
            'fa-exclamation-triangle': Boolean(this.validationError || this.warning || this.hasRequestError || this.hasClientExistsError),
            'fa-save': Boolean(
                !this.hasSuccessfulRequest &&
                (this.currentFormType === FormType.DUPLICATION ||
                    this.currentFormType === FormType.EDITION ||
                    (this.currentFormType === FormType.CREATION && !this.validationError && !this.warning))
            ),
            'fa-pencil': Boolean(!this.hasSuccessfulRequest && this.currentFormType === FormType.EDITION),
            'fa-trash': Boolean(!this.hasSuccessfulRequest && !this.warning && this.currentFormType === FormType.DELETION),
            'fa-check': Boolean(!this.validationError && !this.warning && this.hasSuccessfulRequest),
            'fa-spinner fa-spin': Boolean(this.hasSubmittedRequest && this.isRequestLoading)
        };
    }

    public get isButtonDisabled(): boolean {
        return this.isRequestLoading || (this.hasSuccessfulRequest && this.configuration.getLockOnSuccess());
    }

    public get showRepeatActionLabel(): boolean {
        return !this.configuration.getLockOnSuccess() &&
            this.hasSuccessfulRequest &&
            (this.currentFormType === FormType.DUPLICATION || this.currentFormType === FormType.CREATION);
    }

    public get repeatActionLabel(): string {
        return this.currentFormType === FormType.DUPLICATION ? 'Duplicar otro' : 'Crear otro';
    }

    public initialize(): void {
        const isDemo = this.configuration.isDemoUser();

        this.requestErrorMessages[0] = isDemo
            ? 'Operación no permitida'
            : 'No hemos podido completar la operación. Revisa los datos e inténtalo de nuevo.';
        this.requestErrorMessages[1] = isDemo ? '' : 'Intentar de nuevo';
        this.rebuildFormOperations();
    }

    /**
     * Evaluates the form and sends the request when the payload is valid.
     */
    public submit(): void {
        const form = this.configuration.getForm();
        const model = this.configuration.getModel();

        if (!form || !model) {
            return;
        }

        this.validationError = null;
        this.validationSummary = null;

        this.refreshControlValidity(model);

        if (!model.valid && this.currentFormType !== FormType.DELETION) {
            this.displayErrorsOnButton(form, model);
            return;
        }

        if (this.currentFormType === FormType.DELETION && this.configuration.getRecordHasAssociatedData() && !this.warning) {
            this.warning = 'El registro tiene datos, por lo que se borrará toda la información asociada. <br> ¿Continuar?';
            return;
        }

        this.warning = null;
        this.hasSubmittedRequest = true;
        this.hasCompletedRequest = false;

        const payload = ReactiveFormPayloadSerializer.serialize(
            form,
            model.getRawValue(),
            this.configuration.getSerializerResolvers?.() ?? {},
        );

        if (this.currentFormType === FormType.DUPLICATION) {
            delete payload['id'];
        }

        this.sendRequest(payload as T);
    }

    private get currentFormType(): FormType {
        return this.configuration.getForm()?.type ?? FormType.CREATION;
    }

    private get isRequestLoading(): boolean {
        return this.configuration.getRequest()?.isLoading === true;
    }

    private get hasRequestError(): boolean {
        return this.hasSubmittedRequest && this.configuration.getRequest()?.hasError === true;
    }

    private get hasClientExistsError(): boolean {
        return this.hasSubmittedRequest && this.configuration.getRequest()?.hasErrorCliExists === true;
    }

    private get hasSuccessfulRequest(): boolean {
        return this.hasSubmittedRequest && this.hasCompletedRequest && this.configuration.getRequest()?.hasError === false;
    }

    private get buttonLabels(): Record<FormType, SubmitButtonLabels> {
        return {
            [FormType.CREATION]: ['Guardar y continuar', 'Guardando...', 'Guardado'],
            [FormType.EDITION]: ['Guardar cambios', 'Guardando cambios...', 'Cambios guardados'],
            [FormType.DUPLICATION]: ['Duplicar', 'Duplicando...', 'Duplicado'],
            [FormType.DELETION]: ['Borrar registro', 'Borrando registro...', 'Registro borrado'],
            [FormType.INFORMATIVE]: ['', '', ''],
            [FormType.EXPORT]: ['', '', ''],
            [FormType.GENERATION]: ['Generar informe', 'Generando informe...', 'Informe generado']
        };
    }

    private sendRequest(payload: T): void {
        const request = this.configuration.getRequest();
        const getRequest = this.configuration.getGetRequest();

        if (!request) {
            return;
        }

        request.safePerform(payload);
        request.response((res) => {
            this.hasCompletedRequest = true;
            getRequest?.safePerform();
            this.configuration.emitAfterSend(res);
            this.scheduleRequestCleanup(() => request.unsuscribe());
            this.configuration.emitGoBack();
        });
    }

    private refreshControlValidity(model: FormGroup): void {
        const controls = model.controls as Record<string, AbstractControl>;

        Object.values(controls).forEach((control) => control.updateValueAndValidity({ emitEvent: false }));
        model.updateValueAndValidity({ emitEvent: false });
    }

    private displayErrorsOnButton(form: Form<T>, model: FormGroup): void {
        const errorMessages: Record<string, ValidationErrorFormatter> = {
            required: () => 'es obligatorio.',
            maxlength: () => 'ha excedido la longitud máxima.',
            email: () => 'no es un correo válido.',
            invalidPattern: (errors) => errors['invalidPattern'] ?? '',
            passwordRequired: (errors) => errors['passwordRequired'] ?? '',
            passwordRepeatRequired: (errors) => errors['passwordRepeatRequired'] ?? '',
            passwordMismatch: (errors) => errors['passwordMismatch'] ?? '',
            repeatableRowInvalid: (errors) => errors['repeatableRowInvalid'] ?? '',
            invalidTime: (errors) => errors['invalidTime'] ?? '',
            invalidSpanishId: (errors) => errors['invalidSpanishId'] ?? '',
            spanishIdTaken: (errors) => errors['spanishIdTaken'] ?? '',
        };
        const formOperations = this.getFormOperations(form, model);
        const controls = model.controls as Record<string, AbstractControl>;
        const invalidFields = Object.entries(controls).reduce<string[]>((acc, [field, control]) => {
            if (!control?.invalid) {
                return acc;
            }

            const fieldInfo = formOperations.getFieldByControlName(field) as FormField<T> | null | undefined;
            const errors = control?.errors ?? {};
            const messages = Object.keys(errors)
                .filter((key) => key in errorMessages)
                .map((key) => errorMessages[key]?.(errors as Record<string, string>))
                .join(' ');
            const fieldLabel = String(fieldInfo?.label ?? field).trim() || field;

            acc.push(`${fieldLabel} ${messages || ': Error desconocido.'}`);
            return acc;
        }, []);

        this.validationError = 'Revisa los campos indicados.';
        this.validationSummary = `No hemos podido guardar todavía. Revisa estos campos:<br>- ${invalidFields.join('<br>- ')}`;
        model.markAllAsTouched();
    }

    private getFormOperations(form: Form<T>, model: FormGroup): FormOperations<T> {
        if (!this.formOperations) {
            this.formOperations = new FormOperations(form, model);
        }

        return this.formOperations;
    }

    private rebuildFormOperations(): void {
        const form = this.configuration.getForm();
        const model = this.configuration.getModel();

        this.formOperations = form && model ? new FormOperations(form, model) : null;
    }

    private scheduleRequestCleanup(cleanup: () => void): void {
        if (this.configuration.scheduleRequestCleanup) {
            this.configuration.scheduleRequestCleanup(cleanup);
            return;
        }

        setTimeout(cleanup, REQUEST_CLEANUP_DELAY_MS);
    }
}
